<template lang="pug">
.relative
  .button-tier-container(@click.stop="runAction" :class="tierContainerClass")
    .button-tier(ref="Button")
      .text-tier
        span.tier-name {{ text }}
        .tier-divider(v-if="quantity != null")
        span.tier-quantity(v-if="quantity != null")
          span.tier-quantity-label Cant.
          span.tier-quantity-value {{ quantity }}
          span.material-symbols-outlined.tier-stock-status(
            v-if="showStockVerifyIcon"
          ) {{ isStockVerified ? 'check_circle' : 'cancel' }}
    span.material-symbols-outlined.tier-reviewed-check-corner(v-if="showReviewedCheckIcon") sensors
    shContextMenu(v-if="nameProduct && size" :items="contextMenuItems" @select="onMenuSelect")
    .bar-tier-wrapper
      .bar-tier(:class="{active: isWaiting}")
</template>

<script>
import shContextMenu from '@/components/elements/ContextMenu'
import gqlStock from '@/graphql/stock'
import gqlProduct from '@/graphql/product'
import modals from '@my/store/modals'
import gql from '@my/graphql'

export default {
  name: 'Tier',

  props: {
    text: { type: String, default: 'Enviar' },
    quantity: { type: Number, default: null },
    action: { type: Function, default: () => {} },
    params: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    nameProduct: { type: String, default: '' },
    size: { type: String, default: '' },
    brand: { type: String, default: '' },
    isChecked: { type: Boolean, default: false },
    enable: { type: Boolean, default: false },
    /** Todos los niveles de la talla en bodega (para total y menú confirmar/desconfirmar) */
    tiers: { type: Array, default: () => [] },
    /** Cursor de clic y acción principal al pulsar el tier (p. ej. alistar en órdenes) */
    clickable: { type: Boolean, default: false },
    /** Cuando está revisado (`isChecked`), mostrar check verde esquina en vez del fondo gris (p. ej. Listos) */
    reviewedCornerCheck: { type: Boolean, default: false }
  },

  components: { shContextMenu },

  computed: {
    sizeUnitCount () {
      return this.tiers ? this.tiers.length : 0
    },

    contextMenuItems () {
      const items = [
        { label: 'Restar 1', action: 'subtract' },
        { label: 'Agregar 1', action: 'add' }
      ]

      if (this.nameProduct && this.size) {
        const units = this.sizeUnitCount
        const unitsLabel = units === 1 ? '1 unidad' : `${units} unidades`

        if (this.enable) {
          items.push({ label: `Desconfirmar ${unitsLabel}`, action: 'unverify' })
        } else {
          items.push({ label: `Confirmar ${unitsLabel}`, action: 'verify' })
        }
      }

      return items
    },

    tierContainerClass () {
      const checkedGrey = this.isChecked && !this.reviewedCornerCheck
      return {
        isChecked: checkedGrey,
        'tier-reviewed-check': this.isChecked && this.reviewedCornerCheck,
        'tier-pending-review': this.reviewedCornerCheck && !this.isChecked,
        disabled: this.disabled,
        clickable: this.clickable && !this.disabled
      }
    },
    showReviewedCheckIcon () {
      return this.isChecked && this.reviewedCornerCheck
    },

    showStockVerifyIcon () {
      return this.nameProduct && this.size
    },

    isStockVerified () {
      return this.enable
    }
  },

  data () {
    return {
      isWaiting: false
    }
  },

  methods: {
    async runAction () {
      if (this.disabled) return

      if (this.isWaiting) return

      this.isWaiting = true
      await this.action(...this.params)
      this.isWaiting = false
    },

    async onMenuSelect (item) {
      if (item.action === 'subtract') await this.acceptDelete()
      if (item.action === 'add') await this.acceptAdd()
      if (item.action === 'verify') await this.setStockVerified(true)
      if (item.action === 'unverify') await this.setStockVerified(false)
    },

    async acceptDelete () {
      if (!this.nameProduct || !this.size || !this.text) return

      const response = await gql.mutate(gqlStock.DELETE_OF_TIER, {
        reference: this.nameProduct,
        size: this.size,
        quantity: 1,
        tier: this.text
      })

      if (response && response.data && response.data.deleteOfTier) {
        modals.showMessage({ message: `Se ha eliminado un producto ${this.nameProduct} del nivel ${this.text} en la talla ${this.size}` })
        this.$emit('deleted')
      }
    },

    async acceptAdd () {
      if (!this.nameProduct || !this.size || !this.text) return

      const response = await gql.mutate(gqlStock.ADD_TO_TIER, {
        reference: this.nameProduct,
        tier: this.text,
        size: this.size
      })

      if (response && response.data && response.data.addToTier) {
        modals.showMessage({ message: `Se ha agregado un producto ${this.nameProduct} al nivel ${this.text} en la talla ${this.size}` })
        this.$emit('added')
      }
    },

    async setStockVerified (enable) {
      if (!this.nameProduct || !this.size || !this.brand) return

      const response = await gql.mutate(gqlProduct.ENABLE_LAST_PRODUCT, {
        nameProduct: this.nameProduct,
        size: this.size,
        brand: this.brand,
        enable
      })

      if (response && response.data && response.data.enableLastProduct) {
        if (enable) modals.showMessage({ message: `Se confirmó el stock restante de ${this.nameProduct} en talla ${this.size}` })
        if (!enable) modals.showMessage({ message: `Se desconfirmó el stock restante de ${this.nameProduct} en talla ${this.size}` })
        this.$emit('enable', enable)
      }
    }
  }
}
</script>

<style lang="stylus">
@import '~@/stylus/functions.styl'

.button-tier-container
  display inline-flex
  position relative
  min-height 3.75rem
  min-width 10.75rem
  width auto
  border 1px solid rgb(220, 220, 220)
  align-items center
  borderRadius(.5rem)
  padding .5rem .75rem .5rem 1.1rem
  cursor default

.button-tier-container.clickable
  cursor pointer

.button-tier-container.isChecked
  background-color rgba(0, 0, 0, .05)

.button-tier-container.tier-reviewed-check
  overflow visible

.button-tier-container.tier-pending-review
  border-color #e6a700

.tier-reviewed-check-corner
  position absolute
  top 0
  right .6rem
  font-size 2.8rem
  line-height 1
  font-weight 600
  color #4CAF50
  pointer-events none
  z-index 4

.button-tier
  display inline-flex
  flex 1
  min-width 0
  align-self stretch
  align-items center
  noSelect()
  background transparent
  position relative
  font-size 1.15rem
  text-transform uppercase

.text-tier
  display flex
  flex-direction column
  align-items flex-start
  justify-content center
  gap .25rem
  position relative
  z-index 2
  line-height 1.15
  text-align left

.tier-name
  font-size 1.15rem

.tier-divider
  width 100%
  height 1px
  background rgba(0, 0, 0, .15)
  margin .1rem 0

.tier-quantity
  display inline-flex
  align-items center
  gap .35rem
  font-size 1rem
  line-height 1
  min-height 1.2rem

.tier-quantity-label
  color rgba(0, 0, 0, .32)
  font-weight 500
  text-transform none

.tier-quantity-value
  display flex
  align-items center
  line-height 1
  color rgba(0, 0, 0, .32)
  font-weight 600

.tier-stock-status
  display inline-flex
  align-items center
  justify-content center
  flex-shrink 0
  width 1.35rem
  height 1.35rem
  font-size 1.35rem
  line-height 1
  color rgba(0, 0, 0, .32)

.button-tier-container.disabled
  cursor default

.button-tier-container.disabled .tier-name
  opacity .3

.button-tier-container.disabled .tier-quantity-label,
.button-tier-container.disabled .tier-quantity-value,
.button-tier-container.disabled .tier-stock-status
  color var(--color-red)
  font-weight normal

.button-tier-container.disabled .bar-tier-wrapper
  opacity .3

.bar-tier-wrapper
  position absolute
  bottom 0
  left 0
  right 0
  height 1px
  overflow hidden

.bar-tier
  width 100%
  height 100%
  background-color rgb(100, 100, 100)
  position relative
  left -100%
  animation bar 1s 0s infinite forwards ease-in-out
  animation-name none

.bar-tier.active
  animation-name bar

.button-tier-container .context-menu
  margin-left auto
  margin-right .15rem
  align-self center

.button-tier-container .context-menu-dots
  padding .2em .15em .2em .2em
</style>



// WEBPACK FOOTER //
// src/components/elements/Tier.vue